/**
 * Accessible modal shell with focus trap, ESC to close, backdrop dismiss, scroll lock, and ARIA.
 * Architectural Context: Pure UI organism. Compound API: Dialog.Header, Dialog.Body, Dialog.Footer—
 * context supplies titleId and onClose automatically.
 *
 * @param open - Controls visibility
 * @param onOpenChange - Called when open state should change (close, backdrop click, ESC)
 * @param closeOnBackdropClick - Whether clicking the backdrop closes the dialog
 * @param maxWidth - Responsive max-width; full on mobile, constrained on sm+
 * @default closeOnBackdropClick true
 * @default maxWidth 'lg'
 *
 * @example
 * <Dialog open={open} onOpenChange={setOpen} title="Confirm" description="Are you sure?">
 *   <Dialog.Header title="Confirm" showCloseButton />
 *   <Dialog.Body>Content here</Dialog.Body>
 *   <Dialog.Footer align="right"><Button>OK</Button></Dialog.Footer>
 * </Dialog>
 */

import React, { useEffect, useRef, useId, createContext, useContext, useMemo } from 'react';
import { Backdrop } from '../../atoms/Backdrop';
import { Typography } from '../../atoms/Typography';
import { DialogHeader, type DialogHeaderProps } from './DialogHeader';
import { DialogBody, type DialogBodyProps } from './DialogBody';
import { DialogFooter, type DialogFooterProps } from './DialogFooter';
import type { DialogProps } from './Dialog.types';
import { maxWidthClasses } from './Dialog.styles';

// Shared with compound parts for aria-labelledby/describedby and close behavior
const DialogContext = createContext<{
  titleId: string;
  descriptionId: string;
  onOpenChange: (open: boolean) => void;
} | null>(null);

const useDialogContext = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('Dialog compound components must be used within Dialog');
  }
  return context;
};

/** Tab cycles focus within the dialog; no escape to page content. */
const useFocusTrap = (open: boolean, dialogRef: React.RefObject<HTMLDivElement | null>) => {
  useEffect(() => {
    if (!open || !dialogRef.current) return;

    const dialog = dialogRef.current;
    const focusableElements = dialog.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // No focusable children: focus container, Tab stays inside
    if (focusableElements.length === 0) {
      dialog.focus();
      dialog.setAttribute('tabindex', '-1');
      return;
    }

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    // Prefer explicit autofocus / first text field over the header close button.
    const preferred =
      dialog.querySelector<HTMLElement>('[data-autofocus], [autofocus]') ??
      dialog.querySelector<HTMLElement>('input:not([type="hidden"]), textarea, select') ??
      firstElement;
    preferred?.focus();

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [open, dialogRef]);
};

/** Root: Backdrop + modal shell. Header/Body/Footer are compound parts wired via context. */
const DialogComponent: React.FC<DialogProps> = ({
  open,
  onOpenChange,
  title: _title,
  children,
  closeOnBackdropClick = true,
  maxWidth = 'lg',
  className = '',
  contentClassName = '',
  description,
  titleId: providedTitleId,
  backdropZIndex = 50,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const generatedTitleId = useId();
  const titleId = providedTitleId || generatedTitleId;
  const descriptionId = useId();

  useFocusTrap(open, dialogRef);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onOpenChange]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onOpenChange(false);
    }
  };

  const contextValue = useMemo(() => ({
    titleId,
    descriptionId,
    onOpenChange,
  }), [titleId, descriptionId, onOpenChange]);

  if (!open) return null;

  return (
    <DialogContext.Provider value={contextValue}>
      <Backdrop
        isOpen={true}
        onClick={handleBackdropClick}
        zIndex={backdropZIndex}
        aria-hidden={false}
        portal={true}
      >
        <div
          ref={dialogRef}
          className={`bg-white rounded-lg sm:rounded-xl shadow-xl mx-auto relative max-h-[95vh] sm:max-h-[90vh] flex flex-col my-2 sm:my-4 md:my-6 z-10 ${maxWidthClasses[maxWidth]} ${className}`}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
        >
          {/* Title is in Dialog.Header; description links via aria-describedby */}
          {description && (
            <Typography as="p" variant="body-sm" weight='medium' id={descriptionId} className="sr-only">
              {description}
            </Typography>
          )}
          <div className={`px-5 sm:px-6 pb-6 pt-4 flex flex-col gap-3 sm:gap-4 flex-1 min-h-0 ${contentClassName}`}>
            {children}
          </div>
        </div>
      </Backdrop>
    </DialogContext.Provider>
  );
};

/** Supplies titleId and onClose from context to DialogHeader. */
const DialogHeaderCompound: React.FC<Omit<DialogHeaderProps, 'id'>> = (props) => {
  const { titleId, onOpenChange: contextOnOpenChange } = useDialogContext();
  const handleClose = props.onClose ?? (() => contextOnOpenChange(false));
  return <DialogHeader {...props} id={titleId} onClose={handleClose} />;
};

const DialogBodyCompound: React.FC<DialogBodyProps> = (props) => {
  return <DialogBody {...props} />;
};

const DialogFooterCompound: React.FC<DialogFooterProps> = (props) => {
  return <DialogFooter {...props} />;
};

export const Dialog = Object.assign(DialogComponent, {
  Header: DialogHeaderCompound,
  Body: DialogBodyCompound,
  Footer: DialogFooterCompound,
});

export type { DialogProps };
